import { useEffect } from 'react';

const SITE_URL = ((import.meta.env?.VITE_SITE_URL as string | undefined) || 'https://cloudprepper.dev').replace(/\/+$/, '');

interface DocumentMetaOptions {
    /** Canonical path for this page, e.g. "/blog/some-post". Omit to leave the canonical untouched. */
    path?: string;
    /** Adds <meta name="robots" content="noindex,follow"> (login, register, 404). */
    noindex?: boolean;
}

type Restore = () => void;

/** Find or create a <head> tag, set attributes, and return a function that puts it back. */
function upsertHeadTag(selector: string, tagName: 'meta' | 'link', attrs: Record<string, string>): Restore {
    let el = document.head.querySelector<HTMLElement>(selector);
    const created = !el;
    if (!el) {
        el = document.createElement(tagName);
        document.head.appendChild(el);
    }
    const previous: Record<string, string | null> = {};
    for (const [name, value] of Object.entries(attrs)) {
        previous[name] = el.getAttribute(name);
        el.setAttribute(name, value);
    }
    const target = el;
    return () => {
        if (created) {
            target.remove();
            return;
        }
        for (const [name, value] of Object.entries(previous)) {
            if (value === null) target.removeAttribute(name);
            else target.setAttribute(name, value);
        }
    };
}

/**
 * Sets document title, meta description, canonical URL, robots and Open Graph tags for the
 * current page. Lightweight stand-in for react-helmet. Everything is restored on unmount so
 * client-side navigation never leaves one page's metadata on another. The prerender step
 * (scripts/prerender.mjs) snapshots these tags into the static HTML Google crawls.
 */
export function useDocumentMeta(title: string, description: string, options: DocumentMetaOptions = {}) {
    const { path, noindex } = options;

    useEffect(() => {
        const previousTitle = document.title;
        document.title = title;

        const restores: Restore[] = [
            upsertHeadTag('meta[name="description"]', 'meta', { name: 'description', content: description }),
            upsertHeadTag('meta[property="og:title"]', 'meta', { property: 'og:title', content: title }),
            upsertHeadTag('meta[property="og:description"]', 'meta', { property: 'og:description', content: description }),
            upsertHeadTag('meta[property="og:type"]', 'meta', { property: 'og:type', content: 'website' }),
        ];

        if (path) {
            const url = `${SITE_URL}${path === '/' ? '/' : path}`;
            restores.push(
                upsertHeadTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: url }),
                upsertHeadTag('meta[property="og:url"]', 'meta', { property: 'og:url', content: url })
            );
        }
        if (noindex) {
            restores.push(upsertHeadTag('meta[name="robots"]', 'meta', { name: 'robots', content: 'noindex,follow' }));
        }

        return () => {
            document.title = previousTitle;
            restores.reverse().forEach((restore) => restore());
        };
    }, [title, description, path, noindex]);
}
